import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  MenuItem,
  TextField,
  Pagination,
  CircularProgress,
} from '@mui/material';
import auditLogService, { AuditLogEntry } from '../services/auditLogService';
import { useToast } from '../hooks/useToast';

const EVENT_COLORS: Record<string, 'success' | 'error' | 'default'> = {
  login_success: 'success',
  login_failed: 'error',
  logout: 'default',
};

const AuditLog: React.FC = () => {
  const { t } = useTranslation();
  const { showError } = useToast();
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [eventFilter, setEventFilter] = useState<string>('');

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await auditLogService.getAuditLogs(page, eventFilter || undefined);
      setLogs(response.data);
      setLastPage(response.pagination.last_page);
    } catch (err) {
      showError(t('auditLog.failedToLoad', 'Failed to load the audit log'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, eventFilter]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const eventLabel = (event: string) => t(`auditLog.event.${event}`, event);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{t('navigation.auditLog', 'Audit Log')}</Typography>
        <TextField
          select
          size="small"
          label={t('auditLog.filterByEvent', 'Event')}
          value={eventFilter}
          onChange={(e) => { setPage(1); setEventFilter(e.target.value); }}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">{t('common.all', 'All')}</MenuItem>
          <MenuItem value="login_success">{t('auditLog.event.login_success', 'Login')}</MenuItem>
          <MenuItem value="login_failed">{t('auditLog.event.login_failed', 'Failed login')}</MenuItem>
          <MenuItem value="logout">{t('auditLog.event.logout', 'Logout')}</MenuItem>
        </TextField>
      </Box>

      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : logs.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
              {t('auditLog.empty', 'No audit log entries found')}
            </Typography>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('auditLog.event.label', 'Event')}</TableCell>
                      <TableCell>{t('common.email', 'Email')}</TableCell>
                      <TableCell>{t('auditLog.user', 'User')}</TableCell>
                      <TableCell>{t('auditLog.ipAddress', 'IP Address')}</TableCell>
                      <TableCell>{t('common.date', 'Date')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>
                          <Chip
                            size="small"
                            label={eventLabel(log.event)}
                            color={EVENT_COLORS[log.event] || 'default'}
                          />
                        </TableCell>
                        <TableCell>{log.email || '—'}</TableCell>
                        <TableCell>{log.user?.full_name || log.user_id || '—'}</TableCell>
                        <TableCell>{log.ip_address || '—'}</TableCell>
                        <TableCell>{new Date(log.created_at).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {lastPage > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={lastPage}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AuditLog;
